import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ubicacionService, { Ubicacion, etiquetaUbicacionSeleccion } from '../services/ubicacionService';
import ubicacionXUsuarioService, { UbicacionXUsuario } from '../services/ubicacionXUsuarioService';
import { isAdminRole } from '../utils/ubicacionUsuarioRoles';

const mapAsignacionToUbicacion = (item: UbicacionXUsuario): Ubicacion => ({
  nroUbicacion: item.nroUbicacion,
  nombreUbicacion: item.nombreUbicacion ?? `Ubicación ${item.nroUbicacion}`,
  nroSuc: 0,
});

export interface UbicacionesUsuarioState {
  ubicaciones: Ubicacion[];
  preferida: string | null;
  preferidaNro: number | null;
  preferidaUbicacion: Ubicacion | null;
  loading: boolean;
  error: string | null;
  sinAsignaciones: boolean;
  esAdmin: boolean;
  refresh: () => Promise<void>;
}

export function useUbicacionesUsuario(): UbicacionesUsuarioState {
  const { user } = useAuth();
  const esAdmin = useMemo(() => isAdminRole(user?.roles), [user?.roles]);
  const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
  const [preferidaNro, setPreferidaNro] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sinAsignaciones, setSinAsignaciones] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ubicacionXUsuarioService.getMias();
      const mapped = (data.items ?? []).map(mapAsignacionToUbicacion);
      setUbicaciones(mapped);
      setPreferidaNro(data.preferidaNroUbicacion ?? null);
      setSinAsignaciones(!data.esAdmin && data.sinAsignaciones);
    } catch (err: unknown) {
      if (esAdmin) {
        try {
          const todas = await ubicacionService.getUbicaciones();
          setUbicaciones(Array.isArray(todas) ? todas : []);
          setPreferidaNro(null);
          setSinAsignaciones(false);
          setError(null);
          return;
        } catch {
          // fallthrough
        }
      }
      setUbicaciones([]);
      setPreferidaNro(null);
      setSinAsignaciones(!esAdmin);
      setError(err instanceof Error ? err.message : 'Error al cargar ubicaciones del usuario');
    } finally {
      setLoading(false);
    }
  }, [esAdmin]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const preferida = useMemo(() => {
    if (preferidaNro == null) return null;
    const found = ubicaciones.find((u) => u.nroUbicacion === preferidaNro);
    return found ? etiquetaUbicacionSeleccion(found) : null;
  }, [preferidaNro, ubicaciones]);

  const preferidaUbicacion = useMemo(() => {
    if (preferidaNro == null) return ubicaciones[0] ?? null;
    return ubicaciones.find((u) => u.nroUbicacion === preferidaNro) ?? ubicaciones[0] ?? null;
  }, [preferidaNro, ubicaciones]);

  return {
    ubicaciones,
    preferida,
    preferidaNro,
    preferidaUbicacion,
    loading,
    error,
    sinAsignaciones,
    esAdmin,
    refresh,
  };
}
